import { AssessmentStatement, ScaleValue, Stage } from '../models/assessment.model';

export const SCALE_OPTIONS: { value: ScaleValue; label: string }[] = [
  { value: 5, label: 'Always like me' },
  { value: 4, label: 'Very much like me' },
  { value: 3, label: 'Like me' },
  { value: 2, label: 'Not much like me' },
  { value: 1, label: 'Not like me at all' },
];

export const PRACTICE_ITEM = { id: 'practice', text: 'I enjoy eating salad.' };

const st = (n: number, stage: Stage, text: string): AssessmentStatement =>
  ({ id: 'st-' + String(n).padStart(2, '0'), order: n, text, stage });

/**
 * 36 statements — 9 per preference.
 *
 * Balanced by preference because the instrument is: whoever scores it compares the four, so an
 * extra item in one would tilt it on item count alone. (The previous 45 ran 11 / 11 / 11 / 12,
 * leaving Implement with that edge.) Nothing here scores — the frontend just collects answers.
 *
 * Ordered so the four preferences rotate (develop → clarify → implement → ideate), keeping
 * consecutive statements from feeling like a themed block.
 */
export const STATEMENTS_SEED: AssessmentStatement[] = [
  st(1,  'dv', 'I like testing and revising my ideas before coming up with the final solution or product.'),
  st(2,  'cl', 'I like taking the time to clarify the exact nature of the problem.'),
  st(3,  'im', 'I enjoy taking the necessary steps to put my ideas into action.'),
  st(4,  'id', 'I like to look at things from many different angles before settling on an idea.'),
  st(5,  'dv', 'I enjoy perfecting an idea until it really works.'),
  st(6,  'cl', 'I like to know the history of a problem before working on it.'),
  st(7,  'im', 'I like to get things done rather than talk about them.'),
  st(8,  'id', 'I enjoy brainstorming lots of options before choosing a direction.'),
  st(9,  'dv', 'I like to plan the steps it takes to make an idea real.'),
  st(10, 'cl', 'I like to gather facts before forming an opinion.'),
  st(11, 'im', 'I enjoy the energy of a deadline.'),
  st(12, 'id', 'I like playing with possibilities.'),
  st(13, 'dv', 'I enjoy comparing alternative solutions carefully.'),
  st(14, 'cl', 'I want to understand why something happened, not just what happened.'),
  st(15, 'im', 'I prefer action over analysis.'),
  st(16, 'id', 'I enjoy imagining how things could be different.'),
  st(17, 'dv', 'I like refining rough ideas into workable plans.'),
  st(18, 'cl', 'I like to define success before starting the work.'),
  st(19, 'im', 'I push to see things finished.'),
  st(20, 'id', 'New ideas excite me more than proven ones.'),
  st(21, 'dv', 'I like to explore the strengths and weaknesses of a potential solution.'),
  st(22, 'cl', 'I enjoy gathering information to identify the root causes of a particular problem.'),
  st(23, 'im', 'I get restless when a project stalls in discussion.'),
  st(24, 'id', 'I enjoy connecting ideas that seem unrelated.'),
  st(25, 'dv', 'I enjoy the analysis and effort it takes to transform a rough concept into a workable idea.'),
  st(26, 'cl', 'I like a clear picture of the challenge before generating solutions.'),
  st(27, 'im', 'I volunteer to take the first step.'),
  st(28, 'id', 'I generate more ideas than most people around me.'),
  st(29, 'dv', 'I enjoy working out the kinks in a promising idea.'),
  st(30, 'cl', 'I feel compelled to ask questions until I have a complete understanding of the issue.'),
  st(31, 'im', 'I feel unsettled when things are left undone.'),
  st(32, 'id', 'I like to work with unique ideas.'),
  st(33, 'dv', 'I like to develop systems that make things more efficient.'),
  st(34, 'cl', 'I like to check my assumptions before committing to a plan.'),
  st(35, 'im', 'I make things happen.'),
  st(36, 'id', 'I enjoy using metaphors and analogies to come up with new ideas for problems.'),
];

// Exact legacy option lists (docs/screen/assessment/). The landing form branches on `industry`:
// selecting "Student" shows fieldOfStudy and hides orgLevel + jobFunction.
export const DEMOGRAPHIC_OPTIONS = {
  educationLevel: ['High school or equivalent', 'Trade school', 'Associate’s degree', 'Bachelor’s degree',
    'Master’s degree', 'Doctorate or professional degree'],
  fieldOfStudy: ['Arts & Design', 'Business & Management', 'Communications', 'Education', 'Engineering',
    'Health & Medical Sciences', 'Humanities', 'General Studies', 'Law & Public Policy', 'Sciences',
    'Social Sciences', 'Technology', 'Trades', 'Not listed', 'Not yet declared'],
  // 'Student' is deliberately absent: it moved to jobFunction ("Work"), where it leads the list
  // and branches the survey to field-of-study.
  industry: ['Arts / Entertainment', 'Education', 'Finance', 'Government', 'Healthcare', 'Manufacturing',
    'Nonprofit', 'Professional Services', 'Real Estate / Construction', 'Retail / Consumer Services',
    'Technology', 'Other'],
  orgLevel: ['Individual Contributor (no direct reports)', 'Manager / Supervisor',
    'Senior Manager / Department Head', 'Director', 'VP / Senior VP', 'Executive / C-level',
    'Self-employed', 'Not Applicable'],
  // Rendered as "Work". 'Student' leads the list and is the branch trigger, so it stays first.
  jobFunction: ['Student', 'Administrative Services', 'Advertising', 'Communications', 'Consulting',
    'Customer Relationship', 'Design', 'E-commerce / Web', 'Educator (K-12)', 'Educator (Higher Ed)',
    'Engineering / Architecture', 'Facilities Operations and Services', 'Finance', 'General Executive',
    'Healthcare Provider', 'Human Resources', 'Information Technology', 'Insurance',
    'Legal and Regulatory Affairs', 'Maintenance', 'Marketing', 'Operations', 'Production / Processing',
    'Project Management', 'Protective Services', 'Public Affairs', 'Public Relations', 'Purchasing',
    'Quality', 'Research and Development', 'Sales', 'Skilled Trades', 'Social Services', 'Not Listed'],
  birthYear: [...Array.from({ length: 60 }, (_, i) => String(2008 - i)), 'Prefer not to say'], // 2008 … 1949 + opt-out
  gender: ['Female', 'Male', 'Non-Binary', 'Not Listed'],
};

export const LANGUAGES = ['English', 'Spanish', 'French', 'Dutch', 'German', 'Italian', 'Portuguese'];

export const ASSESSMENT_PRICE = { amount: 59, currency: 'USD', label: 'FourSight Thinking Profile' };

/** Peak stage → profile-type name; ties fall back to Integrator (mock simplification). */
export const PROFILE_TYPE_FOR: Record<Stage, string> = {
  cl: 'Clarifier', id: 'Ideator', dv: 'Developer', im: 'Implementer',
};
